import React from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Cross2Icon } from '@radix-ui/react-icons';
import { FiAlertTriangle, FiUsers, FiUser, FiBook, FiHelpCircle, FiClock, FiFileText, FiInfo, FiArrowRight } from 'react-icons/fi';
import axios from 'axios';
import { getApiUrl, getAuthHeaders } from '../../config/api.config';
import './LimitReachedModal.css';

const LimitReachedModal = ({ 
  show, 
  onHide, 
  limitType, 
  resourceName, 
  limit, 
  planType, 
  currentCount, 
  upgradeRecommendation,
  grandfathered = false 
}) => {
  
  const getPlanDisplayName = (plan) => {
    switch (plan) {
      case 'personal': return 'Personal Learning Plan';
      case 'classroom': return 'Classroom Success Plan';
      case 'institution': return 'Institution Growth Plan';
      default: return 'Current Plan';
    }
  };

  const getUpgradeDisplayName = (plan) => {
    switch (plan) {
      case 'classroom': return 'Classroom Success Plan';
      case 'institution': return 'Institution Growth Plan';
      default: return 'Higher Plan';
    }
  };

  const getUpgradeFeatures = (fromPlan, toPlan) => {
    if (fromPlan === 'personal' && toPlan === 'classroom') {
      return [
        'Up to 30 students (vs 1)',
        'Unlimited classes (vs 5)',
        'Unlimited questions (vs 50)',
        'Priority support',
        'Advanced analytics'
      ];
    }
    if (toPlan === 'institution') {
      return [
        'Up to 100 students',
        'Up to 20 teachers',
        '1 Admin account',
        'Unlimited everything',
        'Premium support',
        'Custom integrations'
      ];
    }
    return ['Enhanced limits', 'More features', 'Better support'];
  };

  const limitMessages = {
    teacher: {
      title: 'Teacher Limit Reached',
      message: `You've reached the maximum number of ${limit} teachers allowed in your trial account.`,
      icon: <FiUser className="limit-icon error" />,
      isTrialMessage: true
    },
    student: {
      title: 'Student Limit Reached',
      message: planType ? 
        `You've reached the maximum number of ${limit} students allowed in your ${getPlanDisplayName(planType)}.` :
        `You've reached the maximum number of ${limit} students allowed in your trial account.`,
      icon: <FiUsers className="limit-icon error" />,
      isTrialMessage: !planType
    },
    class: {
      title: 'Class Limit Reached',
      message: planType ? 
        `You've reached the maximum number of ${limit} classes allowed in your ${getPlanDisplayName(planType)}.` :
        `You've reached the maximum number of ${limit} classes allowed in your trial account.`,
      icon: <FiBook className="limit-icon error" />,
      isTrialMessage: !planType
    },
    question: {
      title: 'Question Limit Reached',
      message: planType ? 
        `You've reached the maximum number of ${limit} questions allowed in your ${getPlanDisplayName(planType)}.` :
        `You've reached the maximum number of ${limit} questions allowed in your trial account.`,
      icon: <FiHelpCircle className="limit-icon error" />,
      isTrialMessage: !planType
    },
    questionsPerClass: {
      title: 'Questions Per Class Limit Reached',
      message: planType ? 
        `You've reached the maximum number of ${limit} questions per class allowed in your ${getPlanDisplayName(planType)}.` :
        `You've reached the maximum number of ${limit} questions per class allowed in your trial account.`,
      icon: <FiHelpCircle className="limit-icon error" />,
      isTrialMessage: !planType
    },
    submission: {
      title: 'Submission Limit Reached',
      message: `You've reached the maximum number of ${limit} submissions allowed in your trial account.`,
      icon: <FiFileText className="limit-icon error" />,
      isTrialMessage: true
    },
    expired: {
      title: 'Trial Period Expired',
      message: 'Your trial period has expired. Please upgrade to continue using all features.',
      icon: <FiClock className="limit-icon warning" />,
      isTrialMessage: true
    },
    generic: {
      title: 'Limit Reached',
      message: `You've reached the ${resourceName || 'feature'} limit for your ${planType ? getPlanDisplayName(planType) : 'trial account'}.`,
      icon: <FiInfo className="limit-icon info" />,
      isTrialMessage: !planType
    }
  };

  const { title, message, icon, isTrialMessage } = limitMessages[limitType] || limitMessages.generic;

  const handleUpgrade = () => {
    // Attempt to create a Stripe portal session so the user can upgrade their subscription.
    // If that fails for any reason, gracefully fall back to the pricing section of the marketing site.
    axios.post(
      getApiUrl('/api/v1/stem@home/payment/create-portal-session'),
      { flowType: 'subscription_update' },
      { headers: getAuthHeaders() }
    )
    .then((response) => {
      if (response.data?.success && response.data?.url) {
        // Redirect straight to the hosted Stripe customer portal.
        window.location.href = response.data.url;
      } else {
        // Fallback: show pricing section.
        window.location.href = '/#pricing';
      }
    })
    .catch(() => {
      // Network or server error — fallback to pricing section.
      window.location.href = '/#pricing';
    });
  };

  const handleContactSales = () => {
    window.open('mailto:francis.jones@snapgrade.ai?subject=Plan Upgrade Inquiry', '_blank');
  };

  return (
    <Dialog.Root open={show} onOpenChange={(open) => !open && onHide()}>
      <Dialog.Portal>
        {/* Overlay */}
        <Dialog.Overlay className="limitOverlay" />
        {/* Content */}
        <Dialog.Content className="limitContent" onEscapeKeyDown={onHide} onPointerDownOutside={onHide}>
          {/* Close button */}
          <Dialog.Close asChild>
            <button className="limitCloseBtn" aria-label="Close">
              <Cross2Icon />
            </button>
          </Dialog.Close>

          {/* Title */}
          <Dialog.Title className="limitTitle">
            {icon}
            <span className="ms-2">{title}</span>
          </Dialog.Title>

          {/* Body */}
          <div className="limitBody">
            <p className="limitMessage">{message}</p>
            
            {grandfathered && (
              <div className="grandfathered-notice">
                <p><strong>Good news:</strong> Your existing content will remain accessible, but you cannot create new {limitType}s until you upgrade.</p>
              </div>
            )}
            
            {currentCount !== undefined && limit !== undefined && (
              <div className="usage-summary">
                <p>Current usage: <strong>{currentCount}</strong> out of <strong>{limit === 'Unlimited' ? '∞' : limit}</strong></p>
              </div>
            )}

            {planType && upgradeRecommendation && (
              <div className="upgrade-section">
                <h5 className="upgrade-title">
                  <FiArrowRight className="me-2" />
                  Upgrade to {getUpgradeDisplayName(upgradeRecommendation)}
                </h5>
                
                <div className="upgrade-features">
                  <p className="upgrade-subtitle">What you'll get:</p>
                  <ul>
                    {getUpgradeFeatures(planType, upgradeRecommendation).map((feature, index) => (
                      <li key={index}>✓ {feature}</li>
                    ))}
                  </ul>
                </div>
                
                {upgradeRecommendation === 'classroom' && (
                  <div className="pricing-info">
                    <p><strong>Pricing:</strong> £29.99/month or £305.91/year</p>
                  </div>
                )}
              </div>
            )}

            {isTrialMessage && (
              <div className="trial-upgrade-section">
                <h5 className="upgrade-title">Ready to upgrade?</h5>
                <p>Choose from our flexible plans designed for different needs:</p>
                <ul>
                  <li><strong>Personal Learning Plan</strong> - £4.99/month (1 teacher, 1 student)</li>
                  <li><strong>Classroom Success Plan</strong> - £29.99/month (1 teacher, 30 students)</li>
                  <li><strong>Institution Growth Plan</strong> - Contact sales (Multiple teachers & admins)</li>
                </ul>
              </div>
            )}
          </div>

          {/* Footer actions */}
          <div className="limitFooter">
            <button className="secondaryBtn" onClick={onHide}>
              Close
            </button>
            
            {planType && upgradeRecommendation === 'classroom' && (
              <button className="primaryBtn" onClick={handleUpgrade}>
                Upgrade to Classroom Plan
              </button>
            )}
            
            {planType && upgradeRecommendation === 'institution' && (
              <button className="primaryBtn" onClick={handleContactSales}>
                Contact Sales
              </button>
            )}
            
            {isTrialMessage && (
              <button className="primaryBtn" onClick={handleUpgrade}>
                View Plans
              </button>
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default LimitReachedModal; 